import type { Complaint } from '~/gql/complaints/complaints'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import { COMPLAINT_CATEGORY_KEYS, COMPLAINT_STATUS_KEYS, COMPLAINT_STATUS_VARIANTS } from './complaint-labels'

type IProps = {
  complaints: readonly Complaint[]
  formatDate: (value: unknown) => string
  onOpen: (complaint: Complaint) => void
}

/** Une ligne par entrée du registre (#325) : reçue le, nature, objet et auteur, état. */
function ComplaintsTable({ complaints, formatDate, onOpen }: IProps) {
  const { t } = useTranslation()

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('complaints.column.receivedAt')}</TableHead>
          <TableHead>{t('complaints.column.category')}</TableHead>
          <TableHead>{t('complaints.column.subject')}</TableHead>
          <TableHead>{t('complaints.column.status')}</TableHead>
          <TableHead><span className="sr-only">{t('complaints.column.actions')}</span></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {complaints.map(c => (
          <TableRow key={c.id}>
            <TableCell className="whitespace-nowrap">{formatDate(c.createdAt)}</TableCell>
            <TableCell>
              {/* Un signalement se repère d'un coup d'œil, dans la couleur d'erreur réservée. */}
              {c.category === 'REPORT'
                ? <Badge variant="error">{t(COMPLAINT_CATEGORY_KEYS[c.category])}</Badge>
                : t(COMPLAINT_CATEGORY_KEYS[c.category])}
            </TableCell>
            <TableCell>
              <div className="flex min-w-0 flex-col">
                <span className="font-medium">{c.subject}</span>
                <span className="text-xs text-muted-foreground">{c.authorName || t('complaints.author.none')}</span>
              </div>
            </TableCell>
            <TableCell>
              <Badge variant={COMPLAINT_STATUS_VARIANTS[c.status]}>{t(COMPLAINT_STATUS_KEYS[c.status])}</Badge>
            </TableCell>
            <TableCell className="text-right">
              <Button variant="outline" size="sm" onClick={() => onOpen(c)} aria-label={t('common.actionFor', { action: t('complaints.open'), name: c.subject })}>
                {t('complaints.open')}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export default ComplaintsTable
